import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { SSRPlaceholderHandlers } from '../vc-observer/observers/ssr-placeholders';
import EntriesTimeline from './entries-timeline';
import getElementName from './get-element-name';
import VCCalculator_FY25_03 from './metric-calculator/fy25_03';
import getViewportHeight from './metric-calculator/utils/get-viewport-height';
import getViewportWidth from './metric-calculator/utils/get-viewport-width';
import ViewportObserver from './viewport-observer';
import WindowEventObserver from './window-event-observer';
const SSRState = {
  normal: 1,
  waitingForFirstRender: 2,
  ignoring: 3
};
const DEFAULT_SELECTOR_CONFIG = {
  id: false,
  testId: true,
  role: false,
  className: false,
  dataVC: true
};
export default class VCObserverNew {
  constructor(config) {
    var _config$isPostInterac, _config$selectorConfi;
    _defineProperty(this, "viewportObserver", null);
    _defineProperty(this, "windowEventObserver", null);
    // SSR related properties
    _defineProperty(this, "ssrPlaceholderHandler", null);
    _defineProperty(this, "ssr", {
      state: SSRState.normal,
      reactRootElement: null,
      renderStart: -1,
      renderStop: -1
    });
    this.entriesTimeline = new EntriesTimeline();
    this.isPostInteraction = (_config$isPostInterac = config.isPostInteraction) !== null && _config$isPostInterac !== void 0 ? _config$isPostInterac : false;
    this.selectorConfig = (_config$selectorConfi = config.selectorConfig) !== null && _config$selectorConfi !== void 0 ? _config$selectorConfi : DEFAULT_SELECTOR_CONFIG;

    // Use shared SSR placeholder handler if provided, otherwise create new one if feature flag is enabled
    if (config.ssrPlaceholderHandler) {
      this.ssrPlaceholderHandler = config.ssrPlaceholderHandler;
    } else {
      var _config$SSRConfig$ena, _config$SSRConfig;
      this.ssrPlaceholderHandler = new SSRPlaceholderHandlers({
        enablePageLayoutPlaceholder: (_config$SSRConfig$ena = (_config$SSRConfig = config.SSRConfig) === null || _config$SSRConfig === void 0 ? void 0 : _config$SSRConfig.enablePageLayoutPlaceholder) !== null && _config$SSRConfig$ena !== void 0 ? _config$SSRConfig$ena : false
      });
    }
    this.viewportObserver = new ViewportObserver({
      onChange: onChangeArg => {
        const {
          time,
          type,
          elementRef,
          visible,
          rect,
          previousRect,
          mutationData
        } = onChangeArg;
        let elementName = 'unknown';
        const element = elementRef.deref();
        if (element) {
          elementName = this.getElementName(element);
        }
        this.entriesTimeline.push({
          time,
          data: {
            type,
            elementName,
            rect,
            previousRect,
            visible,
            attributeName: mutationData === null || mutationData === void 0 ? void 0 : mutationData.attributeName,
            oldValue: mutationData === null || mutationData === void 0 ? void 0 : mutationData.oldValue,
            newValue: mutationData === null || mutationData === void 0 ? void 0 : mutationData.newValue
          }
        });
      },
      // Pass SSR context to ViewportObserver
      getSSRState: () => this.getSSRState(),
      getSSRPlaceholderHandler: () => this.getSSRPlaceholderHandler()
    });
    this.windowEventObserver = new WindowEventObserver({
      onEvent: ({
        time,
        type
      }) => {
        this.entriesTimeline.push({
          time,
          data: {
            type: 'window:event',
            eventType: type
          }
        });
      }
    });
  }
  start(_) {
    var _this$viewportObserve, _window, _this$windowEventObse;
    // Reset SSR state on start (matches old VCObserver behavior)
    this.ssr = {
      state: SSRState.normal,
      reactRootElement: null,
      // Reset to null (matches old VCObserver)
      renderStart: -1,
      renderStop: -1
    };
    (_this$viewportObserve = this.viewportObserver) === null || _this$viewportObserve === void 0 ? void 0 : _this$viewportObserve.start();
    if ((_window = window) !== null && _window !== void 0 && _window.__SSR_ABORT_LISTENERS__) {
      const abortListeners = window.__SSR_ABORT_LISTENERS__;
      const aborts = abortListeners.aborts;
      if (aborts && typeof aborts === 'object') {
        Object.entries(aborts).forEach(([key, time]) => {
          if (typeof time === 'number') {
            this.entriesTimeline.push({
              time,
              data: {
                type: 'window:event',
                eventType: key
              }
            });
          }
        });
      }
    }
    (_this$windowEventObse = this.windowEventObserver) === null || _this$windowEventObse === void 0 ? void 0 : _this$windowEventObse.start();
    this.entriesTimeline.clear();
  }
  stop() {
    var _this$viewportObserve2, _this$windowEventObse2;
    (_this$viewportObserve2 = this.viewportObserver) === null || _this$viewportObserve2 === void 0 ? void 0 : _this$viewportObserve2.stop();
    (_this$windowEventObse2 = this.windowEventObserver) === null || _this$windowEventObse2 === void 0 ? void 0 : _this$windowEventObse2.stop();

    // Clear SSR state on stop (matches old VCObserver behavior)
    this.ssr.reactRootElement = null;
  }

  // SSR related methods
  setReactRootElement(element) {
    this.ssr.reactRootElement = element;
  }
  setReactRootRenderStart(startTime = performance.now()) {
    this.ssr.renderStart = startTime;
    this.ssr.state = SSRState.waitingForFirstRender;
  }
  setReactRootRenderStop(stopTime = performance.now()) {
    this.ssr.renderStop = stopTime;
  }
  collectSSRPlaceholders() {
    // This is handled by the shared SSRPlaceholderHandlers in VCObserverWrapper
    // Individual observers don't need to implement this
  }

  // Internal methods for ViewportObserver to access SSR state
  getSSRState() {
    return this.ssr;
  }
  getSSRPlaceholderHandler() {
    return this.ssrPlaceholderHandler;
  }
  addSSR(ssr) {
    this.entriesTimeline.push({
      time: ssr,
      data: {
        elementName: 'SSR',
        type: 'mutation:element',
        rect: {
          height: getViewportHeight(),
          width: getViewportWidth(),
          left: 0,
          top: 0,
          x: 0,
          y: 0,
          bottom: getViewportHeight(),
          right: getViewportWidth(),
          toJSON() {
            return null;
          }
        },
        visible: true
      }
    });
  }
  async getVCResult(param) {
    const {
      start,
      stop,
      interactionId,
      include3p
    } = param;
    const results = [];
    this.addStartEntry(start);
    const calculator_fy25_03 = new VCCalculator_FY25_03();
    if (param.ssr) {
      this.addSSR(param.ssr);
    }
    const orderedEntries = this.entriesTimeline.getOrderedEntries({
      start,
      stop
    });
    const fy25_03 = await calculator_fy25_03.calculate({
      orderedEntries,
      startTime: start,
      stopTime: stop,
      interactionId,
      isPostInteraction: this.isPostInteraction,
      include3p
    });
    if (fy25_03) {
      results.push(fy25_03);
    }
    return results;
  }
  addStartEntry(startTime) {
    this.entriesTimeline.push({
      time: startTime,
      data: {
        type: 'mutation:element',
        elementName: 'START',
        visible: true,
        rect: {
          x: 0,
          y: 0,
          width: window.innerWidth,
          height: window.innerHeight,
          top: 0,
          left: 0,
          bottom: window.innerHeight,
          right: window.innerWidth,
          toJSON: function () {
            return {
              x: this.x,
              y: this.y,
              width: this.width,
              height: this.height,
              top: this.top,
              left: this.left,
              bottom: this.bottom,
              right: this.right
            };
          }
        }
      }
    });
  }
  getElementName(element) {
    return getElementName(this.selectorConfig, element);
  }
}